"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFeedback = exports.FeedbackContainer = exports.FeedbackContext = exports.CommonFeedbackPlacement = exports.CommonFeedbackType = void 0;
var react_1 = __importStar(require("react"));
var morse_react_1 = require("morse-react");
var react_router_dom_1 = require("react-router-dom");
/**
 * A list of common feedback types.
 *
 * Developers are encouraged to think not about the appearance or position of feedback
 * messages, but rather classify them according to their content or context.
 */
var CommonFeedbackType;
(function (CommonFeedbackType) {
    CommonFeedbackType["RecordCreated"] = "RecordCreated";
    CommonFeedbackType["RecordCreatedError"] = "RecordCreatedError";
    CommonFeedbackType["RecordUpdated"] = "RecordUpdated";
    CommonFeedbackType["RecordUpdatedError"] = "RecordUpdatedError";
    CommonFeedbackType["ImportantActionResult"] = "ImportantActionResult";
    CommonFeedbackType["ImportantActionError"] = "ImportantActionError";
    CommonFeedbackType["ImportantActionWarning"] = "ImportantActionWarning";
    CommonFeedbackType["BackgroundResult"] = "BackgroundResult";
    CommonFeedbackType["BackgroundError"] = "BackgroundError";
    CommonFeedbackType["SystemNotification"] = "SystemNotification";
})(CommonFeedbackType = exports.CommonFeedbackType || (exports.CommonFeedbackType = {}));
/**
 * A list of common placements for feedback with in the UI
 *
 * This is used when configuring the application design system
 * and routing messages of different types to the correct place.
 */
var CommonFeedbackPlacement;
(function (CommonFeedbackPlacement) {
    /**
     * Feedback items routed to the Portal placement will be sited on a React portal
     * and can overlay anywhere on the UI
     */
    CommonFeedbackPlacement["Portal"] = "Portal";
    /**
     * Toasts should be paired with the <Toaster> component
     */
    CommonFeedbackPlacement["Toast"] = "Toast";
    /**
     * Somewhere in the application header
     */
    CommonFeedbackPlacement["ApplicationHeader"] = "ApplicationHeader";
    /**
     * Somewhere in the application footer
     */
    CommonFeedbackPlacement["ApplicationFooter"] = "ApplicationFooter";
    /**
     * Somewhere inline within the displayed page
     */
    CommonFeedbackPlacement["InPage"] = "InPage";
})(CommonFeedbackPlacement = exports.CommonFeedbackPlacement || (exports.CommonFeedbackPlacement = {}));
/**
 * A hook to return a management interface for tracking the collection of feedback items.
 *
 * Most of the heavy lifting is within this hook.
 *
 * @param feedbackFactories The map of factories against feedback types. Normally provided through the factories prop of the FeedbackContainer
 * @returns
 */
var useFeedbackManagement = function (feedbackFactories) {
    // Let useState keep track of our collection of feedback items. Ultimately we need
    // React to re-render our UI when this changes.
    var _a = react_1.useState([]), feedbackCollection = _a[0], setFeedbackCollection = _a[1];
    // Use a ref to keep efficent track of our highest feedback item number.
    var counter = react_1.useRef(0);
    var history = react_router_dom_1.useHistory();
    // Install a listener with the router to facilitate removing
    // feedback items when we navigate.
    react_1.useEffect(function () {
        history.listen(function () {
            purgeClearAfterNavigate();
        });
    }, []);
    // If we just use the collection provided by useState we end up with an issue when
    // we use autoDismiss to trigger dismissal or when we set the timer to remove the element
    // after allowing for dismiss animation.
    //
    // Because our feedbackCollection is immutable (to trigger re-renders) we discover that
    // our setTimeout callbacks end up operating on the list that was present at the time they
    // were first registered.
    //
    // If the user causes multiple overlapping feedback items the UI state could get very
    // confused with items popping in and out of existance.
    //
    // The solution was to synchronise the state to a ref's mutable current value. This allowed
    // the best of both worlds - we could read the list to modify from the ref, but still update
    // the list using setFeedbackCollection to ensure re-renders happened properly.
    var syncCollection = react_1.useRef([]);
    syncCollection.current = feedbackCollection;
    /**
     * Adds feedback to the collection.
     */
    var addFeedback = function (feedback) {
        // If we don't have a registered factory for this type of feedback we
        // need to use the default factory. It's important the user gets their feedback
        // ... right?
        if (!feedbackFactories[feedback.type]) {
            feedback.type = 'default';
        }
        var factory = feedbackFactories[feedback.type];
        counter.current++;
        var newFeedbackMeta = {
            index: counter.current,
            feedback: feedback,
            visible: true,
            placement: factory.placement,
            clearAfterNavigate: factory.clearAfterNavigate,
        };
        // Install our new item with React state
        setFeedbackCollection(__spreadArray(__spreadArray([], feedbackCollection), [newFeedbackMeta]));
        if (factory.autoDismiss) {
            // If our factory wants to auto dismiss, setup a timout to do this.
            setTimeout(function () {
                dismiss(newFeedbackMeta);
            }, factory.autoDismiss);
        }
    };
    /**
     * Removes all feedback that is flagged for removal after navigation events.
     */
    function purgeClearAfterNavigate() {
        setFeedbackCollection(syncCollection.current.filter(function (item) { return !item.clearAfterNavigate; }));
    }
    /**
     * Remove a specific feedback item from the collection, and by consequence the DOM.
     * @param feedback
     */
    function remove(feedback) {
        setFeedbackCollection(syncCollection.current.filter(function (item) { return item.index !== feedback.index; }));
    }
    /**
     * Dismiss a feedback item.
     *
     * This initially just sets the `visible` state to false and then starts a timer
     * to call `remove`. This allows for UI animation of the feedback item disappering.
     *
     * Remove is called after 1 second - so animations must complete in that time.
     * @param feedback
     */
    function dismiss(feedback, noDelay) {
        if (noDelay === void 0) { noDelay = false; }
        // To dismiss feedback we actually just mark it as not visible and then
        // set a timer to remove it completely after about a second.
        //
        // This allows for the removal to be animated.
        setFeedbackCollection(__spreadArray(__spreadArray([], syncCollection.current.filter(function (item) { return item.index !== feedback.index; })), [
            __assign(__assign({}, feedback), { visible: false }),
        ]));
        if (noDelay) {
            remove(feedback);
        }
        else {
            setTimeout(function () {
                // Enough animation already! Let's remove the item.
                remove(feedback);
            }, 1000);
        }
    }
    /**
     * Creates the React element to represent a feedback item.
     *
     * @param feedbackMetaData
     * @returns
     */
    var createElement = function (feedbackMetaData) {
        var factory = feedbackFactories[feedbackMetaData.feedback.type];
        var Component = factory.component;
        var feedbackElement = (
        // Add classes and the key. The key is very important and ensures DOM elements are preserved, allowing
        // animation to be triggered on addition of the +hidden class.
        react_1.default.createElement("div", { key: feedbackMetaData.index, className: 'c-feedback ' + (!feedbackMetaData.visible ? '+hidden' : '') },
            react_1.default.createElement(Component, { feedback: feedbackMetaData.feedback, visible: feedbackMetaData.visible, onDismiss: function (noDelay) { return dismiss(feedbackMetaData, noDelay); } })));
        if (feedbackMetaData.placement === CommonFeedbackPlacement.Portal) {
            // Portals get special treatment as they need wrapped in the Portal component to
            // lift them out their context and get installed at the very outside of the DOM.
            feedbackElement = react_1.default.createElement(morse_react_1.Portal, { key: feedbackMetaData.index }, feedbackElement);
        }
        return feedbackElement;
    };
    /**
     * Returns the list of ReactElements to be displayed in a given placement.
     *
     * @param placement
     * @returns
     */
    var getFeedbackElements = function (placement) {
        return feedbackCollection
            .filter(function (trackedFeedback) { return trackedFeedback.placement == placement; })
            .sort(function (a, b) { return (a.index > b.index ? 1 : -1); })
            .map(function (trackedFeedback) { return createElement(trackedFeedback); });
    };
    return {
        addFeedback: addFeedback,
        getFeedbackElements: getFeedbackElements,
        feedbackCollection: feedbackCollection,
        setFeedbackCollection: setFeedbackCollection,
        dismiss: dismiss,
        remove: remove,
    };
};
/**
 * The feedback context.
 */
exports.FeedbackContext = react_1.default.createContext({});
/**
 * A container that installs the context provider, configures it with factories and
 * outputs all feedback items destined for the 'Portal' placement.
 *
 * @param props
 * @returns
 */
var FeedbackContainer = function (props) {
    var feedbackFactories = __assign(__assign({}, props.factories), { default: props.defaultFactory });
    var feedbackManagement = useFeedbackManagement(feedbackFactories);
    return (react_1.default.createElement(exports.FeedbackContext.Provider, { value: feedbackManagement },
        react_1.default.createElement(exports.FeedbackContext.Consumer, null, function (manager) {
            return manager.getFeedbackElements(CommonFeedbackPlacement.Portal);
        }),
        props.children));
};
exports.FeedbackContainer = FeedbackContainer;
/**
 * Returns the feedback context.
 */
var useFeedback = function () {
    return react_1.useContext(exports.FeedbackContext);
};
exports.useFeedback = useFeedback;
//# sourceMappingURL=useFeedback.js.map